class ArticleSidebar {
  constructor(layout) {
    this.layout = layout;
    this.content = layout.querySelector('[data-article-content]');
    this.toc = layout.querySelector('[data-article-toc]');
    this.list = layout.querySelector('[data-article-toc-list]');

    if (!this.content || !this.toc || !this.list) return;

    this.headings = Array.from(this.content.querySelectorAll('h2, h3')).filter(
      (heading) => heading.textContent.trim() !== ''
    );

    if (!this.headings.length) return;

    this.build();
    this.toc.hidden = false;
    this.observe();
  }

  build() {
    const usedIds = new Set(Array.from(document.querySelectorAll('[id]')).map((element) => element.id));
    let topLevel = 0;
    let nested = 0;

    this.headings.forEach((heading, index) => {
      if (!heading.id) {
        const base = this.slugify(heading.textContent) || `article-heading-${index + 1}`;
        let id = base;
        let suffix = 2;

        while (usedIds.has(id)) {
          id = `${base}-${suffix}`;
          suffix += 1;
        }

        heading.id = id;
        usedIds.add(id);
      }

      if (heading.tagName === 'H2') {
        topLevel += 1;
        nested = 0;
      } else {
        nested += 1;
      }

      const item = document.createElement('li');
      const link = document.createElement('a');
      const number = heading.tagName === 'H3' && topLevel > 0 ? `${topLevel}.${nested}` : `${topLevel || index + 1}`;

      item.className = `article-toc__item article-toc__item--${heading.tagName.toLowerCase()}`;
      link.href = `#${heading.id}`;
      link.dataset.tocNumber = number;
      link.textContent = heading.textContent.trim();
      item.appendChild(link);
      this.list.appendChild(item);
    });
  }

  observe() {
    if (!('IntersectionObserver' in window)) return;

    const links = Array.from(this.list.querySelectorAll('a'));
    const linkById = new Map(links.map((link) => [decodeURIComponent(link.hash.slice(1)), link]));
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];

        if (!visible) return;
        const activeLink = linkById.get(visible.target.id);
        if (!activeLink || activeLink === this.activeLink) return;

        links.forEach((link) => link.classList.remove('is-active'));
        activeLink.classList.add('is-active');
        this.activeLink = activeLink;
        this.keepActiveContextVisible(activeLink);
      },
      { rootMargin: '-15% 0px -70% 0px' }
    );

    links.forEach((link) => {
      link.addEventListener('click', () => {
        links.forEach((item) => item.classList.remove('is-active'));
        link.classList.add('is-active');
        this.activeLink = link;
        this.keepActiveContextVisible(link);
      });
    });

    this.headings.forEach((heading) => observer.observe(heading));
  }

  keepActiveContextVisible(activeLink) {
    const activeItem = activeLink.closest('.article-toc__item');
    const nextItem = activeItem?.nextElementSibling;
    if (!activeItem) return;

    const listBounds = this.list.getBoundingClientRect();
    const activeBounds = activeItem.getBoundingClientRect();
    const contextBounds = nextItem?.getBoundingClientRect() || activeBounds;
    const padding = 12;
    const activeIsVisible = activeBounds.top >= listBounds.top + padding;
    const nextIsVisible = contextBounds.bottom <= listBounds.bottom - padding;

    if (activeIsVisible && nextIsVisible) return;

    const targetTop = Math.max(0, this.list.scrollTop + activeBounds.top - listBounds.top - padding);
    this.list.scrollTo({
      top: targetTop,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  }

  slugify(value) {
    return value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  }
}

document.querySelectorAll('[data-article-reading-layout]').forEach((layout) => new ArticleSidebar(layout));
